import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Subject } from "rxjs";
import { ObjectUtils } from "@cv-invision/core";
import { DialogService } from "primeng/dynamicdialog";
import { FieldModel, GenericFormService } from "@cv-invision/cv.invision-views.ux";
import { FormControl, FormGroup } from "@angular/forms";
import { CreateEditFieldValueComponent } from "../components/create-edit-field-value/create-edit-field-value.component";

@Component({
	selector: 'cv-picklist-field',
	templateUrl: './picklist-field.component.html',
	styleUrls: [ './picklist-field.component.scss' ],
})
export class PicklistFieldComponent implements OnInit, OnDestroy {
	@Input() fieldOptions: any;
	@Input() control: FormControl;
	@Input() field: FieldModel;
	@Input() value: any;
	sourceData: any;
	targetData: any;
	isArray = true;

	private atComponentDestroy = new Subject();

	get getTitle(): string {

		if(this.field.UIOptions && 'getTitle' in this.field.UIOptions.PickListOptions) {
			return this.field.UIOptions.PickListOptions.getTitle(this.field.DisplayText);
		}

		return this.field.DisplayText;
	}

	constructor(
		public dialogService: DialogService,
		private formService: GenericFormService
	) {
		this.sourceData = [];
		this.targetData = [];
	}

	ngOnInit(): void {
		const controlValue = this.control.value;
		if (controlValue) {
			if (controlValue instanceof Array) {
				this.isArray = true;
				this.targetData = controlValue.map(item => {
					return {
						label: item,
						value: item
					}
				});
			} else if (controlValue instanceof Object) {
				this.isArray = false;
				let valueData = Object.keys(controlValue);

				if (this.field.UIEvents && 'getData' in this.field.UIEvents) {
					const formattedData = this.field.UIEvents.getData(controlValue);
					this.targetData = formattedData
				} else {
					if ('filters' in this.field.UIOptions && this.field.UIOptions.filters.length > 0) {
						valueData = valueData.filter(item => {
							return !this.field.UIOptions.filters.includes(item);
						});
					}

					this.targetData = valueData.map(item => {
						return {
							label: item,
							value: item
						}
					});
				}
			}

		}
	}

	/**
	 * event to move data from target to source list
	 * @param $event
	 */
	onMoveToSource($event: any): void {
		this.deleteElements($event.items[0]);
		this.onChangeItems();
	}

	/**
	 * event to move all data from target to source list
	 * @param $event
	 */
	onMoveAllToSource($event: any) {
		for (const item of $event.items) {
			this.deleteElements(item);
		}
		this.onChangeItems();
	}

	/**
	 * event to delete elements in the form control
	 * @param item
	 */
	deleteElements(item: any): void {
		const values = this.control.value;

		if (this.isArray) {
			const indexElement = values.findIndex(element => element === item.label);
			values.splice(indexElement, 1);
		} else {
			const path = this.fieldOptions.path;
			const pathElements = path.split(".");
			(this.formService.form.get(pathElements[0]) as FormGroup).removeControl(item.value);
			delete values[item.value];
		}
		this.control.setValue(values);
		;
	}

	/**
	 * event to move all data from source to target list
	 * @param $event
	 */
	onMoveAllToTarget($event: any) {
		const values = this.control.value;

		for (let i = 0; i < $event.items.length; i++) {
			values.push($event.items[i].label);
		}

		this.control.setValue(values);
		this.onChangeItems();
	}

	/**
	 * event to move data from source to target list
	 * @param $event
	 */
	onMoveToTarget($event: any): void {
		this.onChangeItems();
	}

	/**
	 * event to call when items changed in the picklist component
	 */
	onChangeItems(): void {
		const controlValues = this.control.value;

		this.formService.onPickListChange$.next({
			field: this.field,
			fieldOptions: this.fieldOptions,
			event: {
				eventName: 'onChangeItems',
				data: controlValues
			}
		});
	}

	/**
	 * event to open dialog to create option
	 */
	showNewItemDialog(item: any = null, index = -1): void {
		this.dialogService.open(CreateEditFieldValueComponent, {
			header: 'Add ' + this.field.DisplayText,
			width: '21.3rem',
			closeOnEscape: false,
			dismissableMask: false,
			styleClass: 'add-config-type-modal',
			draggable: false,
			data: {
				childForm: this.field.UIOptions.PickListOptions.childForm,
				label: item?.label,
				value: item?.value,
			}
		}).onClose.subscribe((result) => {
			if (result && result.data) {
				const targetData = ObjectUtils.recursiveDeepCopy(this.targetData);

				if (index >= 0) {
					targetData[index] = result.data;
				} else {
					targetData.push(result.data);
				}
				this.targetData = targetData;
				this.setControlValues();
			}
		});
	}

	/**
	 * event to edit item in target list
	 * @param item
	 */
	editItem(item: any) {
		const targetValues = this.targetData;
		const indexElement = targetValues.findIndex(element => element.label === item.label);
		this.showNewItemDialog(item, indexElement);
	}

	/**
	 * event to remove item manually in target list
	 * @param $event
	 * @param item
	 */
	removeItem($event: any, item: any): void {
		this.deleteElements(item);
		const targetValues = this.targetData;
		const indexElement = targetValues.findIndex(element => element.label === item.label);

		if (indexElement > -1) {
			targetValues.splice(indexElement, 1);
		}

		this.targetData = targetValues;
	}

	/**
	 * event to set onchange event in formbuilder configuration
	 */
	setControlValues(): void {
		if (this.field.UIEvents && 'onChange' in this.field.UIEvents) {
			this.field.UIEvents.onChange({value: this.targetData}, this.field, this.formService.form);
		}
	}

	/**
	 * onDestroy event
	 */
	ngOnDestroy(): void {
		this.atComponentDestroy.next({});
		this.atComponentDestroy.unsubscribe();
	}
}
